import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Loader, Message } from 'semantic-ui-react';
import Toolbar from 'components/common/Toolbar';
import ToolbarFooter from 'components/common/Toolbar/ToolbarFooter';
import Transactions from 'components/Wallet/Transactions';
import { INDIVIDUAL_WALLET } from 'constants/walletTransactionsContext';
import Empty from 'components/common/Empty';
import CreditSidebar from 'components/common/SidebarFilter/CreditSidebar';
import DropdownFilterYear from 'components/common/DropdownFilterYear';
import getUserTransactions from 'redux/actions/user/getUserTransactions';
import getSingleUser from 'redux/actions/user/getSingleUser';
import getUserRoles from 'helpers/getUserRoles';
import SingleIndividualWalletChart from 'components/Wallet/SingleIndividualWalletChart';

const SingleIndividualWallet = ({ match }) => {
  const dispatch = useDispatch();
  const [showCreditSidebar, setShowCreditSidebar] = useState(false);
  const [filterYear, setFilterYear] = useState(null);
  const { roles, currentOrgId } = getUserRoles();

  const userId = Number(match?.params?.groupId);

  const { userDetails } = useSelector(
    ({ user: { getSingleUser: singleUser } }) => singleUser,
  );

  const {
    loading,
    data: transactions,
    monthlyBalances,
    error,
    meta,
  } = useSelector(({ user: { getTransactions } }) => getTransactions);

  useEffect(() => {
    const queries = {};
    if (filterYear) queries.year = filterYear;
    getUserTransactions(userId, queries)(dispatch);
    getSingleUser(userId)(dispatch);
  }, [userId, filterYear]);

  const onQuery = queries => {
    const base = { ...queries };
    if (filterYear) base.year = filterYear;
    if (base.year === 'all') delete base.year;
    getUserTransactions(userId, base)(dispatch);
  };

  const userName = userDetails?.first_name
    ? `${userDetails.first_name} ${userDetails.last_name}`
    : '';

  return (
    <div className="wallet">
      <Toolbar
        header={{
          navigation: true,
          title: userName,
          rightComponent: (
            <>
              <DropdownFilterYear
                handleChange={() => {}}
                requestHandledBySelf
                setFilterYear={val =>
                  setFilterYear(val === 'all' ? null : val)
                }
              />
              <Button
                primary
                onClick={() => setShowCreditSidebar(true)}
              >
                Credit
              </Button>
            </>
          ),
        }}
        body={
          <SingleIndividualWalletChart
            individualWallet={transactions?.[0]?.individual_wallet}
            monthlyBalances={monthlyBalances}
            filterYear={filterYear}
          />
        }
        footer={
          <Toolbar
            ignoreHelmet
            header={{
              title: 'All Transactions',
              rightComponent: (
                <ToolbarFooter
                  onSearch={onQuery}
                  onPaginate={onQuery}
                  paginationMeta={meta}
                  exportCSVData={transactions}
                  exportCSVFileName={`${userName}-transactions`}
                  filterTransactions
                  onFilterTransactions={onQuery}
                  filterTransactionsOptions={[
                    'pending',
                    'success',
                    'failed',
                  ]}
                />
              ),
            }}
          />
        }
        backUrl="/wallets/individual"
      />

      <>
        {error && <Message negative>{error.message}</Message>}
        {loading && <Loader active inline="centered" />}

        {!loading && !error && transactions?.length === 0 && (
          <Empty message="No transactions found" />
        )}

        {!loading && !error && transactions?.length > 0 && (
          <Transactions
            currentOrgName={userName}
            items={transactions}
            roles={roles}
            context={INDIVIDUAL_WALLET}
            viewAll
          />
        )}
      </>

      <CreditSidebar
        filterOn={showCreditSidebar}
        setActionType={val => setShowCreditSidebar(!!val)}
        currentOrgId={currentOrgId}
        userId={userId}
      />
    </div>
  );
};

SingleIndividualWallet.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      groupId: PropTypes.string,
    }),
  }).isRequired,
};

export default SingleIndividualWallet;
